import React, { useEffect, useState } from "react";
import NoFooterModal from "../Common/NoFooterModal";
import { Box, Button, TextField, Typography } from "@mui/material";
import useForm from "../Common/FormComponent/useForm";
import FormComponent from "../Common/FormComponent/FormComponent";
import {
  assignmentContainerProps,
  assignmentForm,
  geographyScopeForm,
  initialAssignForm,
} from "./constants";
import { useDispatch, useSelector } from "react-redux";
import { getCityList } from "../../store/actions/City";
import InfiniteScrollDropdown from "../Common/InfiniteScrollDropdown";
import Label from "../Common/Label/Label";
import {
  getBulkLocalities,
  getLocalityList,
} from "../../store/actions/Locality";
import useMutation from "../../hooks/useMutation";
import CommunicationHub from "../../services/api/CommunicationHub";
import { setStatus } from "../../store/actions/User";
import Comparators from "../../constants/FeatureToggle/Comparators";
import RenderIf from "../Common/RenderIf/RenderIf";
import BulkUploadFile from "./BulkCsvUpload";

const validateForm = (values, elementType, activeFile) => {
  const errors = {};
  if (!values?.assignment_type)
    errors.assignment_type = "This field is required";
  if (
    elementType !== "COMMUNICATION_TYPE_VIDEO" &&
    values.assignment_type !== "MANDATORY"
  ) {
    errors.assignment_type =
      "This field needs to be Mandatory for this element type";
  }

  if (!values?.driver_service || values?.driver_service?.length === 0)
    errors.driver_service = "This field is required";

  if (values.type === "geography") {
    if (!values?.entity_type) errors.entity_type = "This field is required";

    if (
      values?.entity_type !== "Country" &&
      (!values?.entity_values || values.entity_values.length === 0)
    )
      errors.entity_values = "This field is required";
  }

  if (values.type === "csv") {
    if (!values.scope) errors.scope = "This is a required field";
    if (values.assignment_type === "MANDATORY" && values.scope !== "driver")
      errors.scope =
        "Only Driver scope is allowed for mandatory assignment type";
    if (!activeFile) errors.file = "Please upload a file";
  }

  return errors;
};

const AssignDialog = ({ options = {}, elementId, elementType }) => {
  const [open, setOpen] = useState(false);

  const form = useForm({});

  useEffect(() => {
    if (open) form.initForm(initialAssignForm(elementId, elementType));
  }, [open]);

  const { entity_type } = form.values;

  const { City, Locality } = useSelector((store) => store);

  const nextPageCity = City.nextPage;
  const nextPageLocality = Locality.nextPage;
  const dispatch = useDispatch();
  const [cityPriorityFlag, setCityPriorityFlag] = useState(true);
  const [localityPriorityFlag, setLocalityPriorityFlag] = useState(false);

  const { mutate, isPending } = useMutation(CommunicationHub.assign);

  const [callback_key, setCallback_key] = useState("");
  const [presign_url, setPresign_url] = useState("");
  const [file_path, setFile_path] = useState("");
  const [activeFile, setActiveFile] = useState("");
  const [activeFileName, setActiveFileName] = useState("");

  useEffect(() => {
    setCityPriorityFlag(true);
    setLocalityPriorityFlag(true);
  }, [form.values.entity_type]);

  const handleClick = async () => {
    try {
      const errors = validateForm(form.values, elementType, activeFile);
      if (Object.values(errors).some((item) => !!item)) {
        dispatch(
          setStatus({
            message: "Please fill all the required fields",
            type: "error",
          })
        );
        form.setAllErrors(errors);
      } else {
        const { entity_values, driver_service } = form.values;
        if (
          form.values.type === "csv" ||
          form.values.assignment_type === "MANDATORY"
        ) {
          if (activeFile) {
            const res = await CommunicationHub.getBulkUploadUrl({
              element_type: elementType,
              element_id: elementId,
              assignment_type: form.values.assignment_type,
              driver_service_ids: (driver_service ?? [])?.map(
                (item) => item.value
              ),
              scope: form.values.scope,
            });

            if (res) {
              setFile_path(res.file_path);
              setCallback_key(res.callback_key);
              setPresign_url(res.file_upload_url);
            } else {
              setStatus({ type: "error", message: "Failed to upload file" });
            }
          }
        } else {
          await mutate({
            ...form.values,
            element_type: elementType,
            conditions: (driver_service ?? []).length
              ? [
                  {
                    name: "service_id",
                    comparator: Comparators.IN,
                    value: (driver_service ?? [])?.map((item) => item.value),
                  },
                ]
              : undefined,
            // ['1'] for india
            entity_values:
              entity_type === "Country"
                ? ["1"]
                : entity_values.map((item) => String(item)),
            driver_service: undefined,
            city: undefined,
          });
        }
        dispatch(
          setStatus({
            message: "Successfully assigned",
            type: "success",
          })
        );
        setOpen(false);
      }
    } catch (err) {
      console.log(err);
      dispatch(
        setStatus({
          message: "Something went wrong",
          type: "error",
        })
      );
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} color="error" variant="text">
        Assign
      </Button>
      {open && (
        <NoFooterModal
          handleClose={() => setOpen(false)}
          header
          heading="Select element type"
        >
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={1}
            alignItems={"center"}
          >
            <Box width="100%">
              <FormComponent
                config={{
                  id: "assignment_type",
                  type: "Dropdown",
                  label: "Assignment Type",
                  containerProps: assignmentContainerProps,
                  props: {
                    required: true,
                    placeholder: "Select assignment type",
                    optionsKey: "assignment_types",
                    disabled: elementType !== "COMMUNICATION_TYPE_VIDEO",
                  },
                }}
                form={form}
                otherOptions={options["assignment_types"]}
              />
              <ErrorLabel label={form.errors?.["assignment_type"]} />
            </Box>

            {assignmentForm.map((item) => (
              <Box width="100%">
                <FormComponent
                  config={item}
                  form={form}
                  key={item.id}
                  otherOptions={options[item?.props?.optionsKey ?? item.id]}
                />
                <ErrorLabel label={form.errors?.[item.id]} />
              </Box>
            ))}

            <RenderIf condition={form.values.assignment_type !== "MANDATORY"}>
              <FormComponent
                config={{
                  id: "type",
                  type: "Radio",
                  containerProps: {
                    sx: {
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      width: "100%",
                      gap: 2,
                    },
                  },
                  props: {
                    options: [
                      {
                        value: "geography",
                        label: "Geography",
                      },
                      {
                        value: "csv",
                        label: "CSV",
                      },
                    ],
                  },
                }}
                form={form}
              />
            </RenderIf>

            <RenderIf
              condition={
                form.values.type === "geography" &&
                form.values.assignment_type !== "MANDATORY"
              }
            >
              {geographyScopeForm.map((item) => (
                <Box width="100%">
                  <FormComponent
                    config={item}
                    form={form}
                    key={item.id}
                    otherOptions={options[item?.props?.optionsKey ?? item.id]}
                  />
                  <ErrorLabel label={form.errors?.[item.id]} />
                </Box>
              ))}

              {entity_type === "Country" && (
                <Box
                  width={"100%"}
                  display={"flex"}
                  sx={{
                    "& > *": {
                      flex: 1,
                    },
                  }}
                >
                  <Label>Selected Country</Label>
                  <TextField
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8px !important",
                      },
                    }}
                    variant="outlined"
                    value={"India"}
                    disabled
                  />
                </Box>
              )}
              {entity_type === "City" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 2,
                    "& > *": {
                      flex: 1,
                    },
                    "& .MuiAutocomplete-root": {
                      paddingRight: "0px !important",
                    },
                  }}
                >
                  <Label>Select City</Label>
                  <Box>
                    <InfiniteScrollDropdown
                      priorityFlag={cityPriorityFlag}
                      setPriorityFlag={setCityPriorityFlag}
                      id="driverCommCityDropdown"
                      getActiveList={(...params) =>
                        dispatch(getCityList(...params))
                      }
                      nextPage={nextPageCity}
                      resKey="cities"
                      modalState="Edit"
                      keyName="city_ids"
                      labelKey="city_name"
                      getDataLabelKey="city.city_name"
                      keyTypeToSet="id"
                      label="City"
                      handleChange={(a, b) =>
                        form.setFields({ entity_values: b })
                      }
                      remoteSearchApplicable
                      remoteSearchKey="startswith"
                      appliedFilters={{}}
                      multiple
                    />
                    <ErrorLabel label={form.errors["entity_values"]} />
                  </Box>
                </Box>
              )}

              {entity_type === "Locality" && (
                <Box
                  width={"100%"}
                  sx={{
                    "& > div": {
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 2,
                      "& > *": {
                        flex: 1,
                      },
                      "& .MuiAutocomplete-root": {
                        paddingRight: "0px !important",
                        marginBottom: "8px !important",
                      },
                    },
                  }}
                >
                  <Box>
                    <Label>Select City</Label>
                    <InfiniteScrollDropdown
                      priorityFlag={cityPriorityFlag}
                      setPriorityFlag={setCityPriorityFlag}
                      id="driverCommCityDropdown"
                      getActiveList={(...params) =>
                        dispatch(getCityList(...params))
                      }
                      nextPage={nextPageCity}
                      resKey="cities"
                      modalState="Edit"
                      keyName="city_ids"
                      labelKey="city_name"
                      getDataLabelKey="city.city_name"
                      keyTypeToSet="id"
                      label="City"
                      handleChange={(a, b) => {
                        form.setFields({ city: b });
                        setLocalityPriorityFlag(true);
                      }}
                      remoteSearchApplicable
                      remoteSearchKey="startswith"
                    />
                  </Box>
                  <Box>
                    <Label>Select Locality</Label>
                    <Box>
                      <InfiniteScrollDropdown
                        priorityFlag={localityPriorityFlag}
                        setPriorityFlag={setLocalityPriorityFlag}
                        id="driverCommLocalityDropdown"
                        getActiveList={(...params) =>
                          dispatch(getLocalityList(...params))
                        }
                        nextPage={nextPageLocality}
                        resKey="localities"
                        modalState="Edit"
                        keyName="locality_ids"
                        labelKey="locality_name"
                        getDataLabelKey="locality.locality_name"
                        keyTypeToSet="id"
                        label="Locality"
                        handleChange={(a, b) =>
                          form.setFields({ entity_values: b })
                        }
                        remoteSearchApplicable
                        appliedFilters={
                          form.values.city && {
                            city_id: form.values.city,
                          }
                        }
                        getActiveItemById={(...params) =>
                          dispatch(getBulkLocalities(...params))
                        }
                        remoteSearchKey="startswith"
                        disabled={!form.values.city}
                        multiple
                      />
                      <ErrorLabel label={form.errors["entity_values"]} />
                    </Box>
                  </Box>
                </Box>
              )}
            </RenderIf>
            <RenderIf
              condition={
                form.values.type === "csv" ||
                form.values.assignment_type === "MANDATORY"
              }
            >
              <Box width="100%">
                <FormComponent
                  config={{
                    id: "scope",
                    type: "Dropdown",
                    label: "Upload Scope",
                    containerProps: assignmentContainerProps,
                    props: {
                      options: [
                        { label: "Driver", value: "driver" },
                        { label: "City", value: "city" },
                        { label: "Locality", value: "locality" },
                      ],
                      required: true,
                      placeholder: "Select upload scope",
                      isOptionDisabled: (option) =>
                        form.values.assignment_type === "MANDATORY" &&
                        option.value !== "driver",
                    },
                  }}
                  form={form}
                />
                <ErrorLabel label={form.errors?.["scope"]} />
              </Box>
              <BulkUploadFile
                instructions={
                  form.values.scope === "driver"
                    ? [
                        "Provide delivery driver IDs (e.g. 34897) as per format given in the sample CSV under entity_id.",
                        "We do not accept FE ids. Eg: FE17823 is not acceptable, but 7823 is acceptable",
                      ]
                    : form.values.scope === "city"
                    ? [
                        "Provide city ids as per format given in the sample CSV under entity_id.",
                      ]
                    : [
                        "Provide locality ids as per format given in the sample CSV under entity_id.",
                      ]
                }
                btnDataTestID="BulkUploadInventoryBtn"
                uploadUrl="/communication_hub/bulk_assign/ack"
                sampleURL="/sample/bulk_assign_communication.csv"
                id="BulkUploadAssignModal"
                sendExtraAck={true}
                keyName="callback_key"
                callback_key={callback_key}
                presign_url={presign_url}
                file_path={file_path}
                activeFile={activeFile}
                activeFileName={activeFileName}
                setActiveFile={setActiveFile}
                setActiveFileName={setActiveFileName}
                validationType="CommunicationHub"
              />
              <ErrorLabel label={form.errors?.["file"]} />
            </RenderIf>
          </Box>
          <Box mt={4} display={"flex"} justifyContent={"end"}>
            <Button
              onClick={() => handleClick()}
              disabled={
                isPending
              }
              color="error"
              variant="contained"
            >
              Confirm
            </Button>
          </Box>
        </NoFooterModal>
      )}
    </>
  );
};

export default AssignDialog;

const ErrorLabel = ({ label }) => {
  if (!label) return null;
  return (
    <Typography
      variant="body2"
      sx={{ fontSize: "14px !important" }}
      color="error"
    >
      {label}
    </Typography>
  );
};
